import React, { useCallback, useEffect, useRef } from 'react';
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined';
import { styled } from '@mui/material/styles';

interface ResizerProps {
  order?: number;
  isVertical?: boolean;
}

const Resizer = styled('div', {
  shouldForwardProp: (propName) => !['order', 'isVertical'].includes(propName as string),
})<ResizerProps>(({ theme, order, isVertical }) => ({
  position: 'relative',
  background: theme.palette.grey[300],
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  order,
  ...(isVertical
    ? {
        height: '100%',
        width: 8,
        cursor: 'col-resize',
        borderLeft: `1px solid ${theme.palette.grey[400]}`,
        borderRight: `1px solid ${theme.palette.grey[400]}`,
      }
    : {
        height: 8,
        width: '100%',
        cursor: 'row-resize',
        borderTop: `1px solid ${theme.palette.grey[400]}`,
        borderBottom: `1px solid ${theme.palette.grey[400]}`,
      }),
}));

interface PaymentWorkspaceResizerProps {
  onResize: (event: MouseEvent) => void;
  onResizeStart?: () => void;
  onResizeEnd?: () => void;
  isVertical?: boolean;
  order?: number;
  className?: string;
}

const PaymentWorkspaceResizer: React.FC<PaymentWorkspaceResizerProps> = ({
  onResize,
  onResizeStart,
  onResizeEnd,
  isVertical,
  order,
  className,
}) => {
  const resizingRef = useRef(false);

  const handleResize = useCallback(
    (event: MouseEvent) => {
      if (!resizingRef.current) return;
      onResize(event);
    },
    [onResize],
  );

  const handleResizeEnd = useCallback(() => {
    if (resizingRef.current) {
      resizingRef.current = false;
      document.body?.classList.remove('UserSelect-none');
      onResizeEnd?.();
    }
  }, [onResizeEnd]);

  const handleResizeStart = useCallback(() => {
    resizingRef.current = true;
    document.body?.classList.add('UserSelect-none');
    onResizeStart?.();
  }, [onResizeStart]);

  useEffect(() => {
    document.addEventListener('mousemove', handleResize);
    document.addEventListener('mouseup', handleResizeEnd);

    return () => {
      document.removeEventListener('mousemove', handleResize);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [handleResize, handleResizeEnd]);

  return (
    <Resizer isVertical={isVertical} order={order} onMouseDown={handleResizeStart} className={className}>
      <DragIndicatorOutlinedIcon sx={{ fontSize: 12, transform: `rotateZ(${isVertical ? 0 : '90deg'})` }} />
    </Resizer>
  );
};

export default PaymentWorkspaceResizer;
